import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "toolbar", "count"]
  static outlets = ["asset-item", "folder-item"]
  static values = {
    projectId: String,
    folderId: String
  }

  // Central state
  #selectedIds = new Set()
  #isDragging = false
  #draggedIds = []
  #lastSelectedIndex = null

  connect() {
    document.addEventListener("keydown", this.#handleKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.#handleKeydown)
  }

  // Outlet lifecycle - handle Turbo Stream reconciliation
  assetItemOutletConnected(outlet, element) {
    // New asset added - ensure table is visible and empty state is hidden
    this.#showTableIfNeeded()
  }

  folderItemOutletConnected(outlet, element) {
    // New folder added - ensure table is visible and empty state is hidden
    this.#showTableIfNeeded()
  }

  assetItemOutletDisconnected(outlet, element) {
    // Asset removed from DOM - prune from selection
    const assetId = element.dataset.assetId
    if (assetId && this.#selectedIds.has(assetId)) {
      this.#selectedIds.delete(assetId)
      this.#updateToolbar()
    }
  }

  // Selection API - called by asset-item controllers via outlet
  select(assetId, options = {}) {
    const { shift = false, meta = false, index = null } = options

    if (shift && this.#lastSelectedIndex !== null && index !== null) {
      // Range selection
      this.#selectRange(index)
    } else if (meta) {
      // Toggle selection (additive)
      if (this.#selectedIds.has(assetId)) {
        this.#selectedIds.delete(assetId)
      } else {
        this.#selectedIds.add(assetId)
      }
      this.#lastSelectedIndex = index
    } else {
      // Single selection - deselect others
      this.#selectedIds.clear()
      this.#selectedIds.add(assetId)
      this.#lastSelectedIndex = index
    }

    this.#updateSelectionVisuals()
    this.#updateToolbar()
  }

  toggleAll(event) {
    const checked = event.target.checked

    this.assetItemOutlets.forEach(outlet => {
      const assetId = outlet.element.dataset.assetId
      if (checked) {
        this.#selectedIds.add(assetId)
      } else {
        this.#selectedIds.delete(assetId)
      }
      outlet.setSelected(checked)
    })

    this.#updateToolbar()
  }

  clearSelection() {
    this.#selectedIds.clear()
    this.#lastSelectedIndex = null
    this.assetItemOutlets.forEach(outlet => outlet.setSelected(false))
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.#updateToolbar()
  }

  // Drag coordination API - called by sortable-list controller
  setDraggedItems(assetIds) {
    this.#draggedIds = assetIds
    this.#isDragging = true
  }

  clearDraggedItems() {
    this.#draggedIds = []
    this.#isDragging = false
  }

  // Bulk Actions
  async deleteSelected() {
    const assetIds = Array.from(this.#selectedIds)
    if (assetIds.length === 0) return

    const confirmed = confirm(
      `Delete ${assetIds.length} selected ${assetIds.length === 1 ? "file" : "files"}? This cannot be undone.`
    )
    if (!confirmed) return

    try {
      const response = await fetch(this.#bulkDeleteUrl(), {
        method: "DELETE",
        headers: this.#headers(),
        body: JSON.stringify({ asset_ids: assetIds })
      })

      if (response.ok) {
        // Optimistic removal
        assetIds.forEach(id => {
          const row = document.querySelector(`[data-asset-id="${id}"]`)?.closest("tr")
          if (row) row.remove()
        })
        this.clearSelection()
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        console.error("Bulk delete failed:", await response.text())
        this.#showToast("Failed to delete items", "error")
      }
    } catch (error) {
      console.error("Bulk delete error:", error)
      this.#showToast("Network error", "error")
    }
  }

  async setPipeline(event) {
    const pipeline = event.currentTarget.dataset.pipeline
    const assetIds = Array.from(this.#selectedIds)

    if (assetIds.length === 0) return

    const settings = this.#pipelineToSettings(pipeline)

    try {
      const response = await fetch(this.#bulkUpdateUrl(), {
        method: "PATCH",
        headers: this.#headers(),
        body: JSON.stringify({ asset_ids: assetIds, settings })
      })

      if (response.ok) {
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        console.error("Bulk update failed:", await response.text())
      }
    } catch (error) {
      console.error("Bulk update error:", error)
    }
  }

  openMoveModal() {
    const assetIds = Array.from(this.#selectedIds)
    if (assetIds.length === 0) return

    this.dispatch("openMoveModal", { detail: { assetIds }, bubbles: true })
  }

  openLightbox(lightboxData, clickedIndex) {
    const assets = this.#collectAllAssetData()
    const lightbox = document.querySelector("[data-controller='asset-lightbox']")
    if (!lightbox) return

    const event = new CustomEvent("lightbox:open", {
      detail: { assets, index: clickedIndex }
    })
    lightbox.dispatchEvent(event)
  }

  // Getters for other controllers
  get selectedIds() {
    return Array.from(this.#selectedIds)
  }

  get selectedCount() {
    return this.#selectedIds.size
  }

  get isDragging() {
    return this.#isDragging
  }

  get draggedIds() {
    return this.#draggedIds
  }

  // Private methods

  #selectRange(toIndex) {
    const fromIndex = this.#lastSelectedIndex
    const start = Math.min(fromIndex, toIndex)
    const end = Math.max(fromIndex, toIndex)

    this.assetItemOutlets.forEach((outlet, index) => {
      if (index >= start && index <= end) {
        const assetId = outlet.element.dataset.assetId
        this.#selectedIds.add(assetId)
        outlet.setSelected(true)
      }
    })

    this.#lastSelectedIndex = toIndex
  }

  #updateSelectionVisuals() {
    this.assetItemOutlets.forEach(outlet => {
      const assetId = outlet.element.dataset.assetId
      outlet.setSelected(this.#selectedIds.has(assetId))
    })

    this.#updateSelectAllState()
  }

  #updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const total = this.assetItemOutlets.length
    const selected = this.#selectedIds.size

    if (selected === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (selected === total) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  #updateToolbar() {
    if (!this.hasToolbarTarget) return

    if (this.#selectedIds.size > 0) {
      this.toolbarTarget.classList.remove("hidden")
      if (this.hasCountTarget) {
        this.countTarget.textContent = this.#selectedIds.size
      }
    } else {
      this.toolbarTarget.classList.add("hidden")
    }

    this.dispatch("selectionChanged", {
      detail: { selectedIds: this.selectedIds, count: this.selectedCount }
    })
  }

  #handleKeydown = (event) => {
    // Only handle if focus is within our element
    if (!this.element.contains(document.activeElement) && document.activeElement !== document.body) {
      return
    }

    // Don't intercept keys when typing in an input or textarea
    const activeTag = document.activeElement?.tagName?.toLowerCase()
    const isTyping = activeTag === "input" || activeTag === "textarea"

    if ((event.metaKey || event.ctrlKey) && event.key === "a" && !isTyping) {
      event.preventDefault()
      this.#selectAllAssets()
    } else if ((event.key === "Delete" || event.key === "Backspace") && this.#selectedIds.size > 0 && !isTyping) {
      event.preventDefault()
      this.deleteSelected()
    } else if (event.key === "Escape") {
      this.clearSelection()
    }
  }

  #selectAllAssets() {
    this.assetItemOutlets.forEach((outlet, index) => {
      const assetId = outlet.element.dataset.assetId
      this.#selectedIds.add(assetId)
      outlet.setSelected(true)
    })

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    }

    this.#updateToolbar()
  }

  #showToast(message, type = "info") {
    console.warn(`[${type}] ${message}`)
  }

  #showTableIfNeeded() {
    const table = document.getElementById("assets-table")
    const emptyState = document.getElementById("assets-empty-state")

    if (table && table.classList.contains("hidden")) {
      table.classList.remove("hidden")
    }
    if (emptyState && !emptyState.classList.contains("hidden")) {
      emptyState.classList.add("hidden")
    }
  }

  #collectAllAssetData() {
    return this.assetItemOutlets.map(outlet => {
      const lightboxBtn = outlet.element.querySelector("[data-lightbox]")
      if (!lightboxBtn) return null

      try {
        return JSON.parse(lightboxBtn.dataset.lightbox)
      } catch {
        return null
      }
    }).filter(Boolean)
  }

  #pipelineToSettings(pipeline) {
    switch (pipeline) {
      case "full":
        return { visible_watermark_enabled: true, invisible_watermark_enabled: true }
      case "visible_only":
        return { visible_watermark_enabled: true, invisible_watermark_enabled: false }
      case "invisible_only":
        return { visible_watermark_enabled: false, invisible_watermark_enabled: true }
      case "passthrough":
        return { visible_watermark_enabled: false, invisible_watermark_enabled: false }
      default:
        return {}
    }
  }

  #bulkUpdateUrl() {
    return `/projects/${this.projectIdValue}/assets/bulk_update`
  }

  #bulkDeleteUrl() {
    return `/projects/${this.projectIdValue}/assets/bulk_destroy`
  }

  #headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
    }
  }
}
